import {
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";
import "./v2.css";
const readPreference = (key: string) => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const persist = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* Preferences are optional. */
  }
};
const ThemeContext = createContext({
  dark: false,
  compact: false,
  toggleTheme: () => {},
  toggleDensity: () => {},
});
export function V2Theme({ children }: { children: ReactNode }) {
  const [dark, setDark] = useState(
    () => readPreference("travallis-v2-theme") === "dark",
  );
  const [compact, setCompact] = useState(
    () => readPreference("travallis-v2-density") === "compact",
  );
  useLayoutEffect(() => {
    const root = document.documentElement;
    const oldVersion = root.getAttribute("data-interface");
    const oldMode = root.getAttribute("data-v2-mode");
    const oldDensity = root.getAttribute("data-v2-density");
    const oldDark = root.classList.contains("dark");
    root.setAttribute("data-interface", "v2");
    root.setAttribute("data-v2-mode", dark ? "dark" : "light");
    root.setAttribute("data-v2-density", compact ? "compact" : "comfortable");
    root.classList.toggle("dark", dark);
    persist("travallis-interface", "v2");
    return () => {
      for (const [key, value] of [
        ["data-interface", oldVersion],
        ["data-v2-mode", oldMode],
        ["data-v2-density", oldDensity],
      ]) {
        if (value === null) root.removeAttribute(key!);
        else root.setAttribute(key!, value!);
      }
      root.classList.toggle("dark", oldDark);
    };
  }, [dark, compact]);
  const value = useMemo(
    () => ({
      dark,
      compact,
      toggleTheme: () =>
        setDark((previous) => {
          persist("travallis-v2-theme", previous ? "light" : "dark");
          return !previous;
        }),
      toggleDensity: () =>
        setCompact((previous) => {
          persist("travallis-v2-density", previous ? "comfortable" : "compact");
          return !previous;
        }),
    }),
    [dark, compact],
  );
  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}
export const useV2Theme = () => useContext(ThemeContext);
